/**
 * PBR decoration for delivered pack assets (VQ-001 / AD-194): the optional
 * per-asset `pbrHints` read + the shadow-flag pass, applied at TEMPLATE load
 * time in the AssetLoader (loader.ts) so every consumer — scene builder,
 * equipment, creatures, rig avatars, NPCs — inherits it through the one seam
 * (clones share the template's materials; clone(true) copies the shadow flags).
 *
 * `pbrHints` is a FRONTEND-ONLY additive read (AD-194 §1): the asset-pack
 * validator already permits unknown props on entries (a deliberate, recorded
 * property — the O-001 batch-2 gate F2 note), so a pack may carry
 * `pbrHints: { metalness?, roughness?, emissiveIntensity? }` per entry with NO
 * shared/ type or validator change — `validate:content` is unaffected, existing
 * content is unchanged, and the shape remains Fable-reserved grammar (adding it
 * to the SHARED type/validator later is a separate, gated change).
 *
 * Everything is duck-typed on three's `.isMesh` / `.isMeshStandardMaterial`
 * flags — no runtime `three` import — so this module is node-safe and tolerant
 * of the loader tests' plain-object glTF stubs (no `traverse` ⇒ no-op).
 */

import type { Object3D } from 'three';

import type { AssetEntry } from '@great-cloud/shared/client';

/** Optional per-asset PBR overrides (AD-194 §1). All fields optional; clamped. */
export interface PbrHints {
  metalness?: number;
  roughness?: number;
  emissiveIntensity?: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function numberField(source: Record<string, unknown>, key: string): number | undefined {
  const value = source[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * Read an entry's optional `pbrHints` (an unknown prop on the frozen AssetEntry
 * shape — see the module header). Non-object / absent ⇒ `null`; numeric fields
 * are clamped (`metalness`/`roughness` to [0,1], `emissiveIntensity` to [0,10]);
 * non-numeric fields are ignored. Returns `null` when nothing valid remains.
 */
export function pbrHintsOf(entry: AssetEntry): PbrHints | null {
  const raw = (entry as unknown as Record<string, unknown>).pbrHints;
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const source = raw as Record<string, unknown>;
  const hints: PbrHints = {};
  const metalness = numberField(source, 'metalness');
  if (metalness !== undefined) hints.metalness = clamp(metalness, 0, 1);
  const roughness = numberField(source, 'roughness');
  if (roughness !== undefined) hints.roughness = clamp(roughness, 0, 1);
  const emissiveIntensity = numberField(source, 'emissiveIntensity');
  if (emissiveIntensity !== undefined) hints.emissiveIntensity = clamp(emissiveIntensity, 0, 10);
  return Object.keys(hints).length > 0 ? hints : null;
}

/** The duck-typed slices of a scene node this module touches. */
interface MaterialLike {
  isMeshStandardMaterial?: boolean;
  metalness?: number;
  roughness?: number;
  emissiveIntensity?: number;
}
interface NodeLike {
  isMesh?: boolean;
  castShadow?: boolean;
  receiveShadow?: boolean;
  material?: MaterialLike | MaterialLike[];
}
type TraversableLike = { traverse?: (cb: (node: unknown) => void) => void };

function eachMaterial(node: NodeLike, apply: (m: MaterialLike) => void): void {
  const material = node.material;
  if (Array.isArray(material)) for (const m of material) apply(m);
  else if (material) apply(material);
}

/**
 * Apply `pbrHints` to every MeshStandardMaterial-family material in a loaded
 * subtree (`isMeshStandardMaterial` is true for MeshPhysicalMaterial too).
 * Null hints, or a root without `traverse` (test stubs), are a no-op.
 */
export function applyPbrHints(root: Object3D, hints: PbrHints | null): void {
  if (hints === null) return;
  const traversable = root as unknown as TraversableLike;
  if (typeof traversable.traverse !== 'function') return;
  traversable.traverse((node) => {
    eachMaterial(node as NodeLike, (m) => {
      if (m.isMeshStandardMaterial !== true) return;
      if (hints.metalness !== undefined) m.metalness = hints.metalness;
      if (hints.roughness !== undefined) m.roughness = hints.roughness;
      if (hints.emissiveIntensity !== undefined) m.emissiveIntensity = hints.emissiveIntensity;
    });
  });
}

/**
 * Enable shadow casting + receiving on every mesh in a loaded subtree (the sun
 * is the only shadow-casting light; the renderer/tier profile decides whether
 * shadows draw at all, so the flags are harmless when shadows are disabled).
 * A root without `traverse` (test stubs) is a no-op.
 */
export function enableShadowFlags(root: Object3D): void {
  const traversable = root as unknown as TraversableLike;
  if (typeof traversable.traverse !== 'function') return;
  traversable.traverse((node) => {
    const mesh = node as NodeLike;
    if (mesh.isMesh === true) {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
    }
  });
}

/** The one-call template decoration the AssetLoader applies after every load. */
export function decorateLoadedAsset(root: Object3D, entry: AssetEntry): void {
  enableShadowFlags(root);
  applyPbrHints(root, pbrHintsOf(entry));
}
